import { classToOption } from "@/lib/util";
import mixpanel from "mixpanel-browser";
import { useUpdateSchoolMutation } from "@/queries/autogenerate/graphql";
import { useState } from "react";
import Select from "react-select";
import styled from "styled-components";
import Error from "./ErrorMessage";
import { CurrentUser } from "./Page";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  & > * {
    margin: 7px;
  }
`;

interface AddStudentToClassProps {
  user: CurrentUser;
  student: NonNullable<CurrentUser["school"]>["users"][number];
  onCompleted?: () => void;
}

const AddStudentToClass: React.FC<AddStudentToClassProps> = ({
  user,
  student,
  onCompleted,
}) => {
  const classes =
    user.school?.classes.filter(
      (c) => !c.users.map((u) => u.id).includes(student.id)
    ) || [];

  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);

  const [{ fetching, error }, updateSchool] = useUpdateSchoolMutation();

  return !classes.length ? (
    <p>
      It seems there are no classes to enrol into yet.
      <span role="img" aria-label="shrug emoji">
        🤷
      </span>
    </p>
  ) : (
    <Container>
      <Error error={error} />
      <div>
        <Select
          isMulti
          isClearable
          onChange={(data) => {
            setSelectedClasses((data || []).map((d) => d.value));
          }}
          options={classes.map(classToOption)}
          noOptionsMessage={() => "No Options"}
          autoFocus={true}
          aria-label="Select a Class"
          placeholder="Select a Class"
          // maxMenuHeight={100}
          styles={{
            container: (provided) => ({
              ...provided,
              width: "350px",
            }),
          }}
        />
      </div>
      {selectedClasses.length ? (
        <button
          className="btn"
          onClick={async () => {
            await updateSchool({
              where: { id: user.school?.id },
              data: {
                classes: {
                  update: selectedClasses.map((id) => ({
                    where: { id },
                    data: {
                      users: {
                        connect: [{ id: student.id }],
                      },
                    },
                  })),
                },
              },
            });

            mixpanel.track("Add student to class(es)");

            if (onCompleted) {
              onCompleted();
            }
          }}
          disabled={fetching}
        >
          {fetching ? <div className="spinner"></div> : null}
          Add Student to Class{selectedClasses.length > 1 ? "es" : ""}
        </button>
      ) : null}
    </Container>
  );
};

export default AddStudentToClass;
